import { Box } from '@material-ui/core';
import { AssetType, Money } from '@nemuru-eng/js-api-client';
import { ReactNode } from 'react';

import { useWidth } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/hooks/useWidth';
import { InsuranceScoreCard } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ConditionsAndProgress/Conditions/components/InsuranceScoreCard';
import { getConditions } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ConditionsAndProgress/Conditions/utils/getLoanConditions';
import { ProcessingStrategyButton } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/ProcessingStrategyButton';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';
import { CustomSkeleton } from '@/components/shared/components/CustomSkeleton';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useScreen } from '@/hooks/useScreen';

interface ConditionsProps<T extends AbstractLoanPaymentMethodDetailEntity> {
  paymentMethodDetailEntity: T;
  editActions?: ReactNode;
}

export const Conditions = <T extends AbstractLoanPaymentMethodDetailEntity>({
  paymentMethodDetailEntity,
  editActions,
}: ConditionsProps<T>) => {
  const { sm } = useScreen();
  const { ref } = useWidth<HTMLDivElement>();
  const { getTextIdTranslation } = useTranslation();

  const paymentMethod = paymentMethodDetailEntity.paymentMethod;

  if (!paymentMethod) {
    return (
      <Box display="flex" flexDirection="column" gridGap={12}>
        <CustomSkeleton height={37} />
        <CustomSkeleton height={44} />
      </Box>
    );
  }

  const loan = paymentMethodDetailEntity.loan;
  const conditions = getConditions(paymentMethod, loan);

  if (!conditions) {
    return (
      <Box display="flex" flexDirection="column" gridGap={12}>
        <CustomSkeleton height={37} />
        <CustomSkeleton height={44} />
      </Box>
    );
  }

  const showInsuranceScoreCard = conditions.insurance || loan?.assetReference[0].type === AssetType.AUTO;

  const principal: Money = {
    value: loan?.loanConditions.principal.amount ?? paymentMethod.amount!,
    currency: paymentMethod.currency,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gridGap: sm ? 12 : 8,
        justifyContent: 'space-between',
      }}
      ref={ref}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: sm ? 'row' : 'column-reverse',
          justifyContent: sm ? 'space-between' : 'flex-start',
          gridGap: sm ? 16 : 8,
          width: '100%',
        }}
      >
        <FlexRow gridGap={6}>
          <Font variant="h2" color="title" style={{ wordBreak: 'break-all' }}>
            {`${conditions.instalmentTotalAmount} ${getTextIdTranslation('PER_MONTH')}`}
          </Font>
          {editActions}
        </FlexRow>
        {/* {loan && <ConditionsSummary loan={loan} applicationType={applicationType} />} */}
        <ProcessingStrategyButton paymentMethodDetailEntity={paymentMethodDetailEntity} />
      </div>
      <Box style={{ overflowX: 'auto', width: '100%' }}>
        <FlexRow gridGap={16} justifyContent="flex-start" flexWrap="wrap">
          <Box minWidth={46} maxWidth={120}>
            <Font variant="body2" color="textSecondary" display="block">
              <Text tId={'TERM'} />
            </Font>
            <Font variant="subtitle1">{conditions.term}</Font>
          </Box>
          {sm && (
            <Box minWidth={64} maxWidth={120}>
              <Font variant="body2" color="textSecondary" display="block">
                <Text tId={'SETUP_FEE_SHORT'} />
              </Font>
              <Font variant="subtitle1">{conditions.setupFee.amount}</Font>
            </Box>
          )}
          <Box minWidth={60} maxWidth={120}>
            <Font variant="body2" color="textSecondary" display="block">
              <Text tId="TIN" />
            </Font>
            <Font variant="subtitle1">{conditions.annualInterest}</Font>
          </Box>
          <Box minWidth={60} maxWidth={120}>
            <Font variant="body2" color="textSecondary" display="block">
              <Text tId="TAE" />
            </Font>
            <Font variant="subtitle1">{conditions.apr}</Font>
          </Box>
          {sm && (
            <Box minWidth={60} maxWidth={120}>
              <Font variant="body2" color="textSecondary" display="block">
                <Text tId="TOTAL" />
              </Font>
              <Font variant="subtitle1">{conditions.totalAmount}</Font>
            </Box>
          )}
          {showInsuranceScoreCard && conditions.insurance && (
            <InsuranceScoreCard insurance={conditions.insurance} term={conditions.term ?? 0} principal={principal} />
          )}
        </FlexRow>
      </Box>
    </div>
  );
};

// interface ConditionsSummaryProps {
//   loan: Loan;
//   applicationType: ApplicationType;
// }

// const ConditionsSummary = ({ loan, applicationType }: ConditionsSummaryProps) => {
//   const { palette } = useCustomTheme();
//   const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

//   return (
//     <>
//       <IconButton
//         size="small"
//         onClick={(event) => {
//           setAnchorEl(event.currentTarget);
//         }}
//       >
//         <Icon size={18} color={palette.text.secondary}>
//           info
//         </Icon>
//       </IconButton>
//       <Menu
//         anchorEl={anchorEl}
//         keepMounted
//         open={Boolean(anchorEl)}
//         onClose={() => setAnchorEl(null)}
//         transformOrigin={{
//           vertical: 0,
//           horizontal: 0,
//         }}
//       >
//         <Box p={2}>
//           <LoanConditionsSummary loan={loan} applicationType={applicationType} showHeader={false} />
//         </Box>
//       </Menu>
//     </>
//   );
// };
